import { Alert, AlertDescription, AlertTitle, Button, Card, CardContent } from "@beweegbaas/ui";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod/v4";
import { LayoutAuth } from "#/layouts/layout.auth";
import {
  confirmEmailChange,
  type ChangeEmailResult,
} from "#/modules/util.confirm-email/change-email.functions";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { m } from "#/paraglide/messages";

// Target of the link in the email-change mail. Under `_authenticated` because the backend applies
// the change to the logged-in user; a logged-out user logs in first and is sent back here.
export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/change-email")({
  validateSearch: z.object({
    email: z.string().optional().catch(undefined),
    token: z.string().optional().catch(undefined),
  }),
  beforeLoad: ({ search, params }) => {
    if (!search.email || !search.token) {
      throw redirect({
        to: "/$locale/{-$schoolyear}",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  component: ChangeEmailPage,
});

function ChangeEmailPage() {
  const { email, token } = Route.useSearch();
  const confirm = useServerFn(confirmEmailChange);
  const router = useRouter();
  const [result, setResult] = useState<ChangeEmailResult | null>(null);
  const started = useRef(false);

  // The ref guard keeps the single-use token from being spent twice under React StrictMode.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        const result = await confirm({ data: { email: email!, token: token! } });
        if (result) setResult(result);
        if (result?.success) void router.invalidate();
      } catch (error) {
        captureUnexpectedError(error, "change-email");
        setResult({ success: false, error: m["common.something_went_wrong"]() });
      }
    })();
  }, [confirm, email, token, router]);

  return (
    <LayoutAuth>
      <title>{m["auth.change_email.page_title"]()}</title>
      <Card className="relative w-full">
        <CardContent>
          {!result ? (
            <p className="text-muted-foreground text-sm">
              {m["auth.change_email.pending"]({ email: email ?? "" })}
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              {result.success ? (
                <Alert variant="default" className="border-none">
                  <IconMaterialSymbolsCheckCircleRounded className="text-green-600!" />
                  <AlertTitle>{m["auth.change_email.success_title"]()}</AlertTitle>
                  <AlertDescription>
                    {m["auth.change_email.success_message"]({ email: result.email })}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive" className="border-none">
                  <IconMaterialSymbolsError className="text-red-500" />
                  <AlertTitle>{m["auth.change_email.failed_title"]()}</AlertTitle>
                  <AlertDescription>{result.error}</AlertDescription>
                </Alert>
              )}
              <Button
                variant={result.success ? "success" : undefined}
                render={
                  <Link
                    from={Route.fullPath}
                    to="/$locale/{-$schoolyear}"
                    params={(prev: Record<string, string>) => prev}
                  />
                }
                nativeButton={false}
              >
                {m["auth.change_email.continue"]()}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
